import { useEffect, useMemo, useRef, useState } from 'react';
import { App, Button, Select, Space, Spin, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { api, errorMessage } from '../api/client';
import { searchOrganizations, useCreateOrganization } from '../features/organizations/api';
import { QuickCreateModal } from './QuickCreateModal';

/** `quickCreate` shows the + button that opens the quick create modal (default on). */
type Props = { disabled?: boolean; allowCreate?: boolean; quickCreate?: boolean } & (
  | { multiple?: false; value?: string | null; onChange?: (v: string | null) => void }
  | { multiple: true; value?: string[] | null; onChange?: (v: string[]) => void }
);

/**
 * Searchable organization picker (one, or several with `multiple`); can create a new organization from the typed
 * name, or with the + button (quick create modal).
 */
export function OrganizationSelect(props: Props) {
  const { disabled, allowCreate = true, quickCreate = true, multiple } = props;
  // the quick create modal, with the name typed so far
  const [creating, setCreating] = useState<{ name?: string } | null>(null);
  const value = props.value;
  const selectedIds = useMemo(() => (Array.isArray(value) ? value : value ? [value] : []), [value]);
  const setValue = (ids: string[]) => (props.multiple ? props.onChange?.(ids) : props.onChange?.(ids[0] ?? null));
  const { message } = App.useApp();
  // search results, and the name of every organization seen (selected ones show by name)
  const [found, setFound] = useState<{ value: string; label: string }[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const resolving = useRef(new Set<string>());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // the search text the current options belong to; differs from `search` while the debounce is pending
  const [settled, setSettled] = useState('');
  const timer = useRef<number>(0);
  const create = useCreateOrganization();

  // Resolve the names of preselected values.
  useEffect(() => {
    for (const id of selectedIds) {
      if (names[id] || resolving.current.has(id)) continue;
      resolving.current.add(id);
      api
        .get<{ id: string; name: string }>(`/organizations/${id}`)
        .then((r) => setNames((prev) => ({ ...prev, [r.data.id]: r.data.name })))
        .catch(() => undefined);
    }
  }, [selectedIds, names]);

  useEffect(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      setLoading(true);
      setFailed(false);
      try {
        const items = await searchOrganizations(search);
        setFound(items.map((o) => ({ value: o.id, label: o.name })));
        setNames((prev) => ({ ...prev, ...Object.fromEntries(items.map((o) => [o.id, o.name])) }));
        setSettled(search);
      } catch {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => window.clearTimeout(timer.current);
  }, [search]);

  // only offer to create once results for exactly this text are in, so existing organizations are not duplicated
  const canCreate = allowCreate && !loading && !failed && settled === search && search.trim().length > 1 && !found.some((o) => o.label.toLowerCase() === search.trim().toLowerCase());
  const CREATE = '__create__';
  const allOptions = useMemo(() => {
    // a single pick keeps its option so the closed select shows its name; several picks show by name as tags
    const single = selectedIds[0];
    const options = !multiple && single && !found.some((o) => o.value === single) ? [{ value: single, label: names[single] ?? '' }, ...found] : found;
    return canCreate ? [...options, { value: CREATE, label: `Create "${search.trim()}"` }] : options;
  }, [found, names, selectedIds, multiple, canCreate, search]);

  const select = (
    <Select
      style={{ flex: 1, minWidth: 0 }}
      showSearch
      allowClear
      mode={multiple ? 'multiple' : undefined}
      disabled={disabled}
      labelInValue
      value={multiple ? selectedIds.map((id) => ({ value: id, label: names[id] ?? '…' })) : selectedIds[0] ? { value: selectedIds[0], label: names[selectedIds[0]] ?? '…' } : undefined}
      placeholder="Search organizations"
      filterOption={false}
      searchValue={search}
      onSearch={setSearch}
      loading={loading || create.isPending}
      notFoundContent={loading ? <Spin size="small" /> : failed ? 'Could not load organizations. Try again.' : search ? 'No matching organizations' : 'No organizations'}
      options={allOptions}
      onChange={async (v: { value: string; label?: unknown } | { value: string; label?: unknown }[] | undefined) => {
        const ids = (Array.isArray(v) ? v : v ? [v] : []).map((o) => o.value);
        setSearch('');
        if (ids.includes(CREATE)) {
          const name = search.trim();
          try {
            const org = await create.mutateAsync({ name });
            setNames((prev) => ({ ...prev, [org.id]: org.name }));
            setValue(ids.map((id) => (id === CREATE ? org.id : id)));
            message.success(`Created ${org.name}`);
          } catch (e) {
            message.error(errorMessage(e));
          }
          return;
        }
        setValue(ids);
      }}
    />
  );
  if (!quickCreate || disabled) return select;
  return (
    <>
      <Space.Compact style={{ width: '100%' }}>
        {select}
        <Tooltip title="New organization">
          <Button icon={<PlusOutlined />} aria-label="New organization" onClick={() => setCreating({ name: search.trim() || undefined })} />
        </Tooltip>
      </Space.Compact>
      <QuickCreateModal
        objectType="ORGANIZATION"
        open={!!creating}
        onClose={() => setCreating(null)}
        preset={creating?.name ? { name: creating.name } : undefined}
        onCreated={(org) => {
          setNames((prev) => ({ ...prev, [org.id]: org.name }));
          setSearch('');
          setValue(multiple ? [...selectedIds.filter((id) => id !== org.id), org.id] : [org.id]);
        }}
      />
    </>
  );
}
